import React from 'react';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import NaturalIcon from '../icons/NaturalIcon';
import HollywoodIcon from '../icons/HollywoodIcon';
import SophisticatedIcon from '../icons/SophisticatedIcon';
import VigorousIcon from '../icons/VigorousIcon';
import YouthfulIcon from '../icons/YouthfulIcon';
import ToothIcon from '../icons/ToothIcon';
import AlignerIcon from '../icons/AlignerIcon';
import BracesIcon from '../icons/BracesIcon';
import CheckCircleIcon from '../icons/CheckCircleIcon';
import CheckIcon from '../icons/CheckIcon';
import SparklesIcon from '../icons/SparklesIcon';
import LightningIcon from '../icons/LightningIcon';
import { GenerationMode, ModelVersion, V2OutputMode } from '../../App';

interface RefineSidebarProps {
    patientName: string;
    setPatientName: (name: string) => void;
    treatmentType: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro';
    setTreatmentType: (type: 'cosmetic' | 'orthodontic' | 'restorative' | 'pro-automated' | 'fast-pro') => void;
    isAutomaticPlanning: boolean;
    setIsAutomaticPlanning: (isAuto: boolean) => void;
    selectedStyles: string[];
    setSelectedStyles: (styles: string[]) => void;
    selectedOrthoStyle: string;
    setSelectedOrthoStyle: (style: string) => void;
    generationMode: GenerationMode;
    setGenerationMode: (mode: GenerationMode) => void;
    isAIDentistEnabled: boolean;
    setIsAIDentistEnabled: (enabled: boolean) => void;
    customPrompt: string;
    setCustomPrompt: (prompt: string) => void;
    handleGenerate: () => void;
    isLoading: boolean;
    handleReset: () => void;
    mouthPath: { x: number; y: number }[] | null;
    // V2 Props
    modelVersion: ModelVersion;
    setModelVersion: (version: ModelVersion) => void;
    v2OutputMode: V2OutputMode;
    setV2OutputMode: (mode: V2OutputMode) => void;
    v2CosmeticStyle: 'natural' | 'hollywood';
    setV2CosmeticStyle: (style: 'natural' | 'hollywood') => void;
}

const suggestionPrompts = [
    "Whiten teeth by two shades",
    "Align the smile symmetrically",
    "Close the small gaps",
    "Repair chipped edges",
];

const smileStylePresets = [
    { name: 'Natural Enhanced', description: 'Believable, healthy, and perfectly imperfect.', icon: <NaturalIcon /> },
    { name: 'Hollywood White', description: 'High-impact, celebrity style. Bright and uniform.', icon: <HollywoodIcon /> },
    { name: 'Sophisticated', description: 'Elegant and mature. Refreshment without looking "done".', icon: <SophisticatedIcon /> },
    { name: 'Vigorous', description: 'Bold and athletic. Defined shapes, conveys strength.', icon: <VigorousIcon /> },
    { name: 'Youthful', description: 'Bright and vibrant. Recaptures a young, unworn look.', icon: <YouthfulIcon /> }
];

const orthoStylePresets = [
    { name: 'Final Aligned Result', description: 'Visualize the perfect alignment after treatment is complete.', icon: <CheckCircleIcon /> },
    { name: 'Simulate with Braces', description: 'See the final result with photorealistic ceramic braces.', icon: <BracesIcon /> },
    { name: 'Simulate with Aligners', description: 'Preview the outcome with clear, discreet aligners.', icon: <AlignerIcon /> }
];

const sectionVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeInOut" } }
};

const RefineSidebar: React.FC<RefineSidebarProps> = ({
    patientName,
    setPatientName,
    treatmentType,
    setTreatmentType,
    isAutomaticPlanning,
    setIsAutomaticPlanning,
    selectedStyles,
    setSelectedStyles,
    selectedOrthoStyle,
    setSelectedOrthoStyle,
    generationMode,
    setGenerationMode,
    isAIDentistEnabled,
    setIsAIDentistEnabled,
    customPrompt,
    setCustomPrompt,
    handleGenerate,
    isLoading,
    handleReset,
    mouthPath,
    modelVersion,
    setModelVersion,
    v2OutputMode,
    setV2OutputMode,
    v2CosmeticStyle,
    setV2CosmeticStyle,
}) => {

    const handleStyleToggle = (styleName: string) => {
        const isSelected = selectedStyles.includes(styleName);
        if (isSelected) {
            setSelectedStyles(selectedStyles.filter(s => s !== styleName));
        } else if (selectedStyles.length < 4) {
            setSelectedStyles([...selectedStyles, styleName]);
        }
    };

    // --- Universal Button Text & Vision Count Logic ---
    let numVisions = 1;
    let buttonText = "Design New Smile";

    // V2 Mode: Simple Unique/Creatives logic
    if (modelVersion === 'v2') {
        numVisions = v2OutputMode === 'creatives' ? 3 : 1;
        buttonText = v2OutputMode === 'creatives' ? 'Design 3 Creatives' : 'Design Unique Smile';
    } else {
        // V1 Mode: Original logic
        const isVariationsMode = generationMode === 'variations' && treatmentType !== 'pro-automated';
        if (isVariationsMode) {
            numVisions = 3;
            buttonText = `Design 3 Smile Variations`;
        } else if (treatmentType === 'cosmetic' && !isAutomaticPlanning && generationMode === 'per-style') {
            numVisions = selectedStyles.length > 0 ? selectedStyles.length : 1;
            buttonText = `Design ${numVisions} Smile Vision${numVisions > 1 ? 's' : ''}`;
        } else if (treatmentType === 'pro-automated') {
            numVisions = 2;
            buttonText = "Design 2 Smile Visions";
        }
    }

    const showVariationsControl = modelVersion === 'v1' && treatmentType !== 'pro-automated';
    const showV2OutputControl = modelVersion === 'v2';
    const isVariationsOptionDisabled = treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length !== 1;
    const isPerStyleOptionDisabled = treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length <= 1;

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full flex flex-col"
        >
            <div className="relative bg-white/80 dark:bg-zinc-900/60 rounded-[2rem] h-full flex flex-col overflow-hidden border border-white/50 dark:border-white/10 shadow-glass dark:shadow-glass-dark backdrop-blur-xl transition-all duration-500">
                {/* Header */}
                <div className="flex-shrink-0 px-8 py-6 border-b border-zinc-100 dark:border-white/5 bg-white/40 dark:bg-white/5 backdrop-blur-md">
                    <div className="flex items-center justify-between mb-1">
                        <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-600 dark:text-teal-400">Step 2 of 2</h2>
                        <div className="h-1 w-12 rounded-full bg-gradient-to-r from-teal-400 to-teal-600" />
                    </div>
                    <div className="flex items-center gap-4">
                        <h1 className="text-3xl font-serif font-bold text-zinc-900 dark:text-white tracking-tight">Refine Simulation</h1>
                        {/* V1/V2 Toggle */}
                        <div className="flex items-center gap-1 p-1 rounded-xl bg-zinc-100 dark:bg-black/40 border border-zinc-200 dark:border-white/10">
                            <button
                                onClick={() => setModelVersion('v1')}
                                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${modelVersion === 'v1'
                                    ? 'bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white shadow-sm'
                                    : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                                    }`}
                            >
                                V1
                            </button>
                            <button
                                onClick={() => setModelVersion('v2')}
                                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${modelVersion === 'v2'
                                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-sm'
                                    : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                                    }`}
                            >
                                V2
                            </button>
                        </div>
                    </div>
                </div>

                {/* Scrollable Content Area */}
                <motion.div
                    className="flex-1 overflow-y-auto px-8 py-6 space-y-8 custom-scrollbar scroll-smooth"
                    variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
                    initial="hidden"
                    animate="visible"
                >
                    {/* 0. Patient Name Input */}
                    <motion.div variants={sectionVariants} className="group">
                        <label htmlFor="patient-name" className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">
                            Patient Name
                        </label>
                        <div className="mt-2 relative">
                            <input
                                id="patient-name"
                                type="text"
                                value={patientName}
                                onChange={(e) => setPatientName(e.target.value)}
                                placeholder="e.g., John Smith"
                                className="w-full px-5 py-3.5 bg-zinc-50/50 dark:bg-black/20 rounded-2xl border border-zinc-200 dark:border-white/10 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all text-sm text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 shadow-sm group-hover:shadow-md"
                            />
                        </div>
                    </motion.div>

                    {/* 1. Treatment Type */}
                    <motion.div variants={sectionVariants}>
                        <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Treatment Type</label>

                        {/* V1 Only: FAST PRO and PRO PLAN buttons */}
                        {modelVersion === 'v1' && (
                            <div className="mt-3 grid grid-cols-2 gap-3 mb-4">
                                <motion.button
                                    onClick={() => setTreatmentType('fast-pro')}
                                    className={`relative overflow-hidden p-4 rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-2 ${treatmentType === 'fast-pro'
                                        ? 'bg-zinc-900 dark:bg-white border-transparent shadow-xl'
                                        : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-teal-500/30'
                                        }`}
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className={`absolute inset-0 opacity-20 ${treatmentType === 'fast-pro' ? 'bg-gradient-to-br from-teal-500 to-purple-600' : ''}`} />
                                    <LightningIcon className={`w-6 h-6 ${treatmentType === 'fast-pro' ? 'text-teal-400 dark:text-teal-600' : 'text-zinc-400'}`} />
                                    <span className={`text-xs font-bold uppercase tracking-widest ${treatmentType === 'fast-pro' ? 'text-white dark:text-zinc-900' : 'text-zinc-600 dark:text-zinc-400'}`}>FAST PRO</span>
                                </motion.button>

                                <motion.button
                                    onClick={() => setTreatmentType('pro-automated')}
                                    className={`relative overflow-hidden p-4 rounded-2xl border transition-all duration-300 flex flex-col items-center justify-center gap-2 ${treatmentType === 'pro-automated'
                                        ? 'bg-zinc-900 dark:bg-white border-transparent shadow-xl'
                                        : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-teal-500/30'
                                        }`}
                                    whileHover={{ scale: 1.02, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className={`absolute inset-0 opacity-20 ${treatmentType === 'pro-automated' ? 'bg-gradient-to-br from-gold-400 to-orange-500' : ''}`} />
                                    <SparklesIcon className={`w-6 h-6 ${treatmentType === 'pro-automated' ? 'text-gold-400 dark:text-gold-600' : 'text-zinc-400'}`} />
                                    <span className={`text-xs font-bold uppercase tracking-widest ${treatmentType === 'pro-automated' ? 'text-white dark:text-zinc-900' : 'text-zinc-600 dark:text-zinc-400'}`}>PRO PLAN</span>
                                </motion.button>
                            </div>
                        )}

                        {/* V2 Enhanced Badge */}
                        {modelVersion === 'v2' && (
                            <div className="mt-3 mb-4 p-3 rounded-xl bg-gradient-to-r from-purple-500/10 to-indigo-500/10 border border-purple-500/20">
                                <div className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 animate-pulse" />
                                    <span className="text-xs font-semibold text-purple-600 dark:text-purple-400">Enhanced Generation Engine Active</span>
                                </div>
                            </div>
                        )}

                        {/* Treatment Type Pills - V1 shows all 3, V2 shows all 3 with special styling */}
                        <div className={`relative flex w-full p-1.5 rounded-2xl border ${modelVersion === 'v2'
                            ? 'bg-gradient-to-r from-purple-500/5 to-indigo-500/5 border-purple-500/20'
                            : 'bg-zinc-100 dark:bg-black/40 border-zinc-200 dark:border-white/5'
                            }`}>
                            {['cosmetic', 'orthodontic', 'restorative'].map((type) => (
                                <button
                                    key={type}
                                    onClick={() => setTreatmentType(type as any)}
                                    className={`relative z-10 flex-1 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors duration-300 ${treatmentType === type
                                        ? modelVersion === 'v2'
                                            ? 'text-white'
                                            : 'text-zinc-900 dark:text-white'
                                        : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                                        }`}
                                >
                                    {type}
                                </button>
                            ))}
                            <motion.div
                                className={`absolute inset-1.5 w-[calc(33.33%-4px)] rounded-xl shadow-sm border ${modelVersion === 'v2'
                                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 border-transparent'
                                    : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-white/10'
                                    }`}
                                layoutId="treatmentTypePill"
                                initial={false}
                                animate={{
                                    left: treatmentType === 'cosmetic' ? '4px' : treatmentType === 'orthodontic' ? 'calc(33.33% + 2px)' : 'calc(66.66% + 0px)'
                                }}
                                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                            />
                        </div>
                    </motion.div>

                    {/* 2. Design Options - V1 ONLY */}
                    {modelVersion === 'v1' && (
                        <motion.div variants={sectionVariants}>
                            <AnimatePresence mode="wait">
                                {treatmentType === 'cosmetic' ? (
                                    <motion.div key="cosmetic-options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} className="space-y-6">
                                        <div>
                                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Design Method</label>
                                            <div className="relative flex w-full p-1.5 mt-3 rounded-2xl bg-zinc-100 dark:bg-black/40 border border-zinc-200 dark:border-white/5">
                                                <button onClick={() => setIsAutomaticPlanning(false)} className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors ${!isAutomaticPlanning ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'}`}>Manual</button>
                                                <button onClick={() => setIsAutomaticPlanning(true)} className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors ${isAutomaticPlanning ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'}`}>Automatic AI</button>
                                                <motion.div
                                                    className="absolute inset-1.5 w-[calc(50%-4px)] bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-zinc-200 dark:border-white/10"
                                                    layoutId="planToggle"
                                                    initial={false}
                                                    animate={{ left: isAutomaticPlanning ? 'calc(50% + 2px)' : '4px' }}
                                                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                                />
                                            </div>
                                        </div>
                                        <AnimatePresence mode="wait">
                                            {isAutomaticPlanning ? (
                                                <motion.div key="auto" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                                                    <div className="p-5 rounded-2xl bg-gradient-to-br from-teal-500/10 to-teal-600/5 border border-teal-500/20 text-teal-800 dark:text-teal-200 text-sm leading-relaxed shadow-inner">
                                                        <div className="flex items-start gap-3">
                                                            <SparklesIcon className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                                                            <p>The AI Planificator will automatically analyze the facial features and dental structure to create the most harmonious smile design plan.</p>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ) : (
                                                <motion.div key="manual" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                                    <div className="flex justify-between items-center mb-3">
                                                        <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Select Styles</label>
                                                        <span className="text-[10px] font-bold bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1 rounded-full text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-white/5">{selectedStyles.length} / 4</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {smileStylePresets.map((preset) => (
                                                            <button key={preset.name} onClick={() => handleStyleToggle(preset.name)} className={`group relative p-4 rounded-2xl border text-left transition-all duration-300 flex flex-col items-start space-y-3 ${selectedStyles.includes(preset.name) ? 'bg-teal-500/5 border-teal-500/50 shadow-lg shadow-teal-900/5' : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-teal-500/30 hover:shadow-md'}`}>
                                                                <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${selectedStyles.includes(preset.name) ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30 scale-110' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400 group-hover:bg-teal-50 dark:group-hover:bg-teal-900/20 group-hover:text-teal-500'}`}>
                                                                    {React.isValidElement(preset.icon) ? React.cloneElement(preset.icon as React.ReactElement<any>, { className: 'w-5 h-5' }) : null}
                                                                </div>
                                                                <div>
                                                                    <p className="font-bold text-sm text-zinc-900 dark:text-white group-hover:text-teal-700 dark:group-hover:text-teal-300 transition-colors">{preset.name}</p>
                                                                </div>
                                                                {selectedStyles.includes(preset.name) && <motion.div layoutId={`check-cosmetic-${preset.name}`} className="absolute top-3 right-3 w-5 h-5 rounded-full bg-teal-500 text-white flex items-center justify-center shadow-sm"><CheckIcon className="w-3 h-3" /></motion.div>}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                ) : treatmentType === 'orthodontic' ? (
                                    <motion.div key="ortho-options" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                                        <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Simulation Style</label>
                                        <div className="mt-3 space-y-3">
                                            {orthoStylePresets.map((preset) => (
                                                <button key={preset.name} onClick={() => setSelectedStyles([preset.name])} className={`w-full relative p-4 rounded-2xl border text-left transition-all duration-300 flex items-center space-x-4 ${selectedStyles[0] === preset.name ? 'bg-teal-500/5 border-teal-500/50 shadow-lg shadow-teal-900/5' : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-teal-500/30 hover:shadow-md'}`}>
                                                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 ${selectedStyles[0] === preset.name ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400'}`}>{React.cloneElement(preset.icon as React.ReactElement<any>, { className: 'w-6 h-6' })}</div>
                                                    <div>
                                                        <p className="font-bold text-sm text-zinc-900 dark:text-white">{preset.name}</p>
                                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">{preset.description}</p>
                                                    </div>
                                                    {selectedStyles[0] === preset.name && <motion.div layoutId="check-ortho" className="absolute top-1/2 -translate-y-1/2 right-4 w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center shadow-sm"><CheckIcon className="w-3.5 h-3.5" /></motion.div>}
                                                </button>
                                            ))}
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div key="other-info" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                                        <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">{treatmentType.charAt(0).toUpperCase() + treatmentType.slice(1)} Simulation</label>
                                        <div className="mt-3 p-5 rounded-2xl bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 text-zinc-600 dark:text-zinc-300 text-sm leading-relaxed">
                                            <p>The AI will perform a fully autonomous analysis and generate a clinically appropriate simulation based on its findings.</p>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </motion.div>
                    )}

                    {/* V2 Simple Mode Description */}
                    {modelVersion === 'v2' && (
                        <motion.div variants={sectionVariants}>
                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">
                                {treatmentType === 'cosmetic' ? 'Aesthetic Treatment' : treatmentType === 'orthodontic' ? 'Orthodontic Treatment' : 'Restorative Treatment'}
                            </label>
                            <div className="mt-3 p-5 rounded-2xl bg-gradient-to-br from-purple-500/10 to-indigo-500/10 border border-purple-500/20 text-purple-800 dark:text-purple-200 text-sm leading-relaxed">
                                <div className="flex items-start gap-3">
                                    <SparklesIcon className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                                    <p>
                                        {treatmentType === 'cosmetic' && 'Professional cosmetic enhancement: whitening, alignment, and aesthetic refinements while preserving natural facial features.'}
                                        {treatmentType === 'orthodontic' && 'Professional orthodontic simulation: alignment correction, spacing adjustment, and bite optimization without changing tooth color.'}
                                        {treatmentType === 'restorative' && 'Professional restorative simulation: repair damaged teeth, replace missing teeth, and restore full dental function.'}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    {/* V2 Cosmetic Style Toggle: Natural vs Hollywood */}
                    {modelVersion === 'v2' && treatmentType === 'cosmetic' && (
                        <motion.div variants={sectionVariants}>
                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Smile Style</label>
                            <div className="mt-3 relative">
                                <button
                                    onClick={() => setV2CosmeticStyle(v2CosmeticStyle === 'natural' ? 'hollywood' : 'natural')}
                                    className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all duration-300 ${v2CosmeticStyle === 'hollywood'
                                        ? 'bg-gradient-to-r from-amber-500/10 to-rose-500/10 border-amber-500/30 shadow-lg shadow-amber-500/10'
                                        : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-purple-500/30 hover:shadow-md'
                                        }`}
                                    role="switch"
                                    aria-checked={v2CosmeticStyle === 'hollywood'}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-300 ${v2CosmeticStyle === 'hollywood'
                                            ? 'bg-gradient-to-br from-amber-400 to-rose-500 text-white shadow-lg shadow-amber-500/30'
                                            : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400'
                                            }`}>
                                            <SparklesIcon className="w-5 h-5" />
                                        </div>
                                        <div className="text-left">
                                            <p className={`font-bold text-sm transition-colors ${v2CosmeticStyle === 'hollywood'
                                                ? 'text-amber-600 dark:text-amber-400'
                                                : 'text-zinc-900 dark:text-white'
                                                }`}>
                                                {v2CosmeticStyle === 'hollywood' ? 'Hollywood Smile' : 'Natural Look'}
                                            </p>
                                            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                                                {v2CosmeticStyle === 'hollywood'
                                                    ? 'Ultra-white, celebrity-grade perfection'
                                                    : 'Subtle, authentic enhancement'
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className={`relative w-14 h-7 rounded-full flex items-center p-1 transition-all duration-300 ${v2CosmeticStyle === 'hollywood'
                                        ? 'bg-gradient-to-r from-amber-400 to-rose-500'
                                        : 'bg-zinc-200 dark:bg-zinc-700'
                                        }`}>
                                        <motion.div
                                            className="w-5 h-5 bg-white rounded-full shadow-md"
                                            layout
                                            animate={{ x: v2CosmeticStyle === 'hollywood' ? 26 : 0 }}
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                        />
                                    </div>
                                </button>
                                <p className="mt-2 text-[10px] text-zinc-400 dark:text-zinc-500 ml-1">
                                    {v2CosmeticStyle === 'hollywood'
                                        ? '✨ Maximum impact: BL1-BL2 ultra-white shade, perfect uniformity'
                                        : '🦷 Natural enhancement: A1-B1 shade, authentic character'
                                    }
                                </p>
                            </div>
                        </motion.div>
                    )}

                    {/* Universal Variations Control (V1 Only) */}
                    {showVariationsControl && (
                        <motion.div variants={sectionVariants}>
                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Number of Visions</label>
                            <div className="relative flex w-full p-1.5 mt-3 rounded-2xl bg-zinc-100 dark:bg-black/40 border border-zinc-200 dark:border-white/5">
                                <button onClick={() => setGenerationMode('per-style')} disabled={isPerStyleOptionDisabled} className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors ${generationMode === 'per-style' ? 'text-zinc-900 dark:text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'} disabled:opacity-50 disabled:cursor-not-allowed`}>
                                    {treatmentType === 'cosmetic' && !isAutomaticPlanning ? '1 Vision per Style' : '1 Vision'}
                                </button>
                                <button
                                    onClick={() => setGenerationMode('variations')}
                                    disabled={true}
                                    title="Temporarily disabled due to high demand"
                                    className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors text-zinc-400 cursor-not-allowed opacity-50`}
                                >
                                    3 Variations (Disabled)
                                </button>
                                <motion.div
                                    className="absolute inset-1.5 w-[calc(50%-4px)] bg-white dark:bg-zinc-800 rounded-xl shadow-sm border border-zinc-200 dark:border-white/10"
                                    layoutId="genModeToggle"
                                    initial={false}
                                    animate={{ left: generationMode === 'variations' ? 'calc(50% + 2px)' : '4px' }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* V2 Output Mode Control */}
                    {showV2OutputControl && (
                        <motion.div variants={sectionVariants}>
                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Output Mode</label>
                            <div className="relative flex w-full p-1.5 mt-3 rounded-2xl bg-gradient-to-r from-purple-500/5 to-indigo-500/5 border border-purple-500/20">
                                <button
                                    onClick={() => setV2OutputMode('unique')}
                                    className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors ${v2OutputMode === 'unique' ? 'text-white' : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
                                        }`}
                                >
                                    Unique (1)
                                </button>
                                <button
                                    onClick={() => setV2OutputMode('creatives')}
                                    disabled={true}
                                    className={`relative z-10 w-1/2 py-2.5 text-[11px] font-bold uppercase tracking-wide rounded-xl transition-colors text-zinc-400 cursor-not-allowed opacity-50`}
                                    title="Temporarily disabled due to high demand"
                                >
                                    3 Creatives (Disabled)
                                </button>
                                <motion.div
                                    className="absolute inset-1.5 w-[calc(50%-4px)] bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl shadow-sm"
                                    layoutId="v2OutputToggle"
                                    initial={false}
                                    animate={{ left: v2OutputMode === 'creatives' ? 'calc(50% + 2px)' : '4px' }}
                                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                                />
                            </div>
                        </motion.div>
                    )}

                    {/* AI Dentist - V1 ONLY */}
                    {modelVersion === 'v1' && (
                        <motion.div variants={sectionVariants}>
                            <label className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Advanced Analysis</label>
                            <button onClick={() => setIsAIDentistEnabled(!isAIDentistEnabled)} className={`mt-3 w-full flex justify-between items-center p-4 rounded-2xl border transition-all duration-300 ${isAIDentistEnabled ? 'bg-teal-500/5 border-teal-500/50 shadow-lg shadow-teal-900/5' : 'bg-white dark:bg-white/5 border-zinc-200 dark:border-white/10 hover:border-teal-500/30 hover:shadow-md'}`} role="switch" aria-checked={isAIDentistEnabled}>
                                <div className="flex items-center gap-4">
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${isAIDentistEnabled ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30' : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-400'}`}><ToothIcon className="w-5 h-5" /></div>
                                    <div className="text-left">
                                        <p className="font-bold text-sm text-zinc-900 dark:text-white">AI Dentist Diagnosis</p>
                                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Get a detailed dental analysis.</p>
                                    </div>
                                </div>
                                <div className={`relative w-12 h-7 rounded-full flex items-center p-1 transition-colors ${isAIDentistEnabled ? 'bg-teal-500' : 'bg-zinc-200 dark:bg-zinc-700'}`}><motion.div className="w-5 h-5 bg-white rounded-full shadow-sm" layout transition={{ type: 'spring', stiffness: 700, damping: 30 }} /></div>
                            </button>
                        </motion.div>
                    )}

                    {/* Additional Instructions - V1 ONLY */}
                    {modelVersion === 'v1' && treatmentType === 'cosmetic' && !isAutomaticPlanning && (
                        <motion.div variants={sectionVariants}>
                            <label htmlFor="custom-prompt" className="text-xs font-bold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider ml-1">Additional Instructions <span className="font-normal text-zinc-400 normal-case tracking-normal">(Optional)</span></label>
                            <textarea id="custom-prompt" rows={2} value={customPrompt} onChange={(e) => setCustomPrompt(e.target.value)} placeholder="e.g., 'Make teeth two shades whiter...'" className="mt-3 w-full p-4 bg-zinc-50/50 dark:bg-black/20 rounded-2xl border border-zinc-200 dark:border-white/10 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 outline-none transition-all text-sm text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-600 shadow-sm" />
                            <div className="mt-3 flex flex-wrap gap-2">
                                {suggestionPrompts.map(suggestion => (<motion.button key={suggestion} onClick={() => setCustomPrompt(customPrompt ? `${customPrompt}, ${suggestion.toLowerCase()}` : suggestion)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-500/10 dark:hover:text-teal-300 transition-colors border border-zinc-200 dark:border-white/5">{suggestion}</motion.button>))}
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Action Bar */}
                <div className="flex-shrink-0 p-8 border-t border-zinc-100 dark:border-white/5 bg-white/40 dark:bg-white/5 backdrop-blur-md">
                    <div className="flex flex-col gap-3">
                        <motion.button
                            onClick={handleGenerate}
                            disabled={isLoading || !mouthPath || (modelVersion === 'v1' && treatmentType === 'cosmetic' && !isAutomaticPlanning && selectedStyles.length === 0)}
                            whileHover={{ scale: 1.02, boxShadow: modelVersion === 'v2' ? "0 20px 40px -10px rgba(139, 92, 246, 0.4)" : "0 20px 40px -10px rgba(20, 184, 166, 0.4)" }}
                            whileTap={{ scale: 0.98 }}
                            className={`relative w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl text-white transition-all duration-300 font-bold uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 shadow-lg overflow-hidden ${modelVersion === 'v2'
                                ? 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-400 hover:to-indigo-500 focus:ring-purple-500/30 shadow-purple-500/20'
                                : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-400 hover:to-teal-500 focus:ring-teal-500/30 shadow-teal-500/20'
                                }`}
                        >
                            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-500" />
                            <SparklesIcon className="w-5 h-5 animate-pulse" />
                            <span className="relative z-10">{buttonText}</span>
                        </motion.button>
                        <motion.button onClick={handleReset} className="w-full text-center py-3 rounded-xl text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-zinc-800 dark:hover:text-white transition-colors">
                            Change Photo
                        </motion.button>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

export default RefineSidebar;
